<template>
	<div class="modal fade" id="figmaAiModal" tabindex="-1" aria-labelledby="figmaAiModalLabel" aria-hidden="true">
		<div class="modal-dialog modal-dialog-scrollable fam-dialog">
			<div class="modal-content">
				<div class="modal-header fam-header justify-content-start">
					<div>
						<h5 class="modal-title fam-title" id="figmaAiModalLabel"><span class="fam-title-icon me-2" aria-hidden="true">✨</span>Figma Variables AI</h5>
						<small class="fam-sub">Describe a change and the assistant will update the theme variables. Changes apply to the preview only — use Save to persist.</small>
					</div>
					<button type="button" class="btn-close fam-close" data-bs-dismiss="modal" aria-label="Close"></button>
				</div>
				<div class="modal-body">
					<div v-if="!hasVariables" class="alert alert-warning mb-0">
						This client has no Figma variables yet. Paste the Figma export into the Figma Variables field first.
					</div>
					<div v-else class="fam-chat" ref="chatArea">
						<div v-if="messages.length === 0 || showPresets" class="fam-presets">
							<div class="fam-presets-label">Quick styles — one click applies a full look:</div>
							<div class="fam-presets-grid">
								<button v-for="preset in presets" :key="preset.title" class="fam-preset" :disabled="loading" @click="usePreset(preset)">
									<span class="fam-preset-swatches">
										<span v-for="color in preset.colors" :key="color" class="fam-swatch fam-swatch--lg" :style="{ background: color }"></span>
									</span>
									<span class="fam-preset-title">{{ preset.title }}</span>
									<span class="fam-preset-desc">{{ preset.desc }}</span>
								</button>
							</div>
							<div class="fam-presets-hint text-muted">...or describe your own change in the box below (e.g. "Make all hero headings bigger on mobile").</div>
						</div>
						<div v-for="(msg, i) in messages" :key="i" class="fam-msg" :class="'fam-msg--' + msg.role">
							<div class="fam-bubble" :class="{ 'fam-bubble--error': msg.error }">
								<div class="fam-text">
									{{ msg.content }}
									<router-link v-if="msg.settingsLink" to="/juniper/settings?tab=ai" class="fam-link" data-bs-dismiss="modal">Open AI settings</router-link>
								</div>
								<div v-if="msg.changes && msg.changes.length" class="fam-changes">
									<button class="fam-changes-toggle" @click="msg.showChanges = !msg.showChanges">
										{{ msg.changes.length }} variable{{ msg.changes.length > 1 ? 's' : '' }}
										<font-awesome-icon :icon="['fas', msg.showChanges ? 'chevron-up' : 'chevron-down']" />
									</button>
									<div v-if="msg.showChanges" class="fam-changes-list">
										<div v-for="change in msg.changes" :key="change.name" class="fam-change">
											<code class="fam-change-name">{{ change.name }}</code>
											<span class="fam-change-values">
												<span class="fam-old"><span v-if="isColor(change.old)" class="fam-swatch" :style="{ background: change.old }"></span>{{ change.old }}</span>
												<font-awesome-icon :icon="['fas', 'arrow-right']" class="fam-arrow" />
												<span class="fam-new"><span v-if="isColor(change.value)" class="fam-swatch" :style="{ background: change.value }"></span>{{ change.value }}</span>
											</span>
										</div>
									</div>
									<div class="fam-actions">
										<button v-if="!msg.applied" class="fam-btn" @click="applyChanges(msg)">
											<font-awesome-icon :icon="['fas', 'check']" class="me-1" />Apply to preview
										</button>
										<span v-else class="fam-applied"><font-awesome-icon :icon="['fas', 'check-circle']" class="me-1" />Applied</span>
									</div>
								</div>
								<div v-if="msg.skipped && msg.skipped.length" class="fam-skipped">
									Skipped unknown variables: {{ msg.skipped.join(', ') }}
								</div>
							</div>
						</div>
						<div v-if="loading" class="fam-msg fam-msg--assistant">
							<div class="fam-bubble">
								<font-awesome-icon :icon="['fas', 'spinner']" spin class="me-2" />Thinking...
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer fam-footer">
					<div class="fam-toolbar" v-if="messages.length || undoStack.length">
						<button v-if="messages.length" class="fam-btn" @click="showPresets = !showPresets" :disabled="loading">
							<font-awesome-icon :icon="['fas', 'swatchbook']" class="me-1" />{{ showPresets ? 'Hide styles' : 'Change style' }}
						</button>
						<button v-if="undoStack.length" class="fam-btn" @click="undo" :disabled="loading">
							<font-awesome-icon :icon="['fas', 'history']" class="me-1" />Undo last apply
						</button>
					</div>
					<div class="fam-input-row">
						<textarea
							v-model="input"
							class="form-control"
							rows="2"
							placeholder="e.g. Change the primary color to #1a7f5c"
							:disabled="loading || !hasVariables"
							@keydown.enter.exact.prevent="send"
						></textarea>
						<div class="fam-input-buttons">
							<button class="fam-btn fam-btn--send" @click="send" :disabled="loading || !hasVariables || !input.trim()" aria-label="Send request">
								<font-awesome-icon :icon="['fas', 'paper-plane']" />
							</button>
							<router-link
								to="/juniper/settings?tab=ai"
								class="fam-btn fam-btn--icon"
								title="AI settings (provider, model, instructions)"
								aria-label="AI settings"
								data-bs-dismiss="modal"
							>
								<font-awesome-icon :icon="['fas', 'cog']" />
							</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'

export default {
	name: 'FigmaAiModal',
	props: ['setting', 'clientId'],
	data() {
		return {
			messages: [],
			input: '',
			loading: false,
			undoStack: [],
			// Original variables snapshot taken before the first preset apply, so
			// switching to another preset always starts from the client's design
			// instead of stacking on top of the previous preset.
			presetBaseline: null,
			// Keep the quick-style chooser visible when the modal first opens, as it
			// is the primary entry point shown in the customizer design.
			showPresets: true,
			// Predefined looks a PM can apply with one click — full redesigns for
			// modern US municipal sites (palette, typography, text scale, spacing,
			// shape, key surfaces). Applied deterministically by the server via
			// juniper-api /figma-ai/preset; `key` must match the backend preset list.
			presets: [
				{
					title: 'Civic Trust',
					key: 'civic-trust',
					desc: 'Navy & sky blue, Inter, dark navbar — classic government look',
					colors: ['#1b3a5c', '#2f80c4', '#e8f1f9'],
				},
				{
					title: 'Evergreen',
					key: 'evergreen',
					desc: 'Forest green, Arsenal serifs, green footer — parks & nature',
					colors: ['#2f6b4f', '#6ea58a', '#eef4f0'],
				},
				{
					title: 'Warm Southwest',
					key: 'warm-southwest',
					desc: 'Terracotta & sand, Poppins, warm buttons — desert-town feel',
					colors: ['#c05b33', '#e0a458', '#faf1e8'],
				},
				{
					title: 'Coastal Breeze',
					key: 'coastal-breeze',
					desc: 'Teal & aqua, Alexandria, teal navbar and pill shapes — waterfront towns',
					colors: ['#17707e', '#2ba3b4', '#eaf6f8'],
				},
			],
		}
	},
	computed: {
		hasVariables() {
			return !!(this.setting && typeof this.setting.value === 'string' && this.setting.value.includes('--'))
		},
	},
	watch: {
		// New setting object means the customizer loaded another client/theme
		setting() {
			this.messages = []
			this.undoStack = []
			this.input = ''
			this.presetBaseline = null
			this.showPresets = true
		},
	},
	methods: {
		async usePreset(preset) {
			if (this.loading || !this.hasVariables) return
			// Presets are applied deterministically on the server (no model),
			// so every click produces the exact advertised look. Changes are
			// always computed against the pre-preset baseline, so picking a
			// different style replaces the previous one instead of stacking.
			this.messages.push({ role: 'user', content: 'Apply preset: ' + preset.title + ' — ' + preset.desc })
			this.loading = true
			this.showPresets = false
			this.scrollChat()
			try {
				const { data } = await juniperApi.post('/figma-ai/preset', {
					variables_css: this.presetBaseline !== null ? this.presetBaseline : this.setting.value,
					preset: preset.key,
				})
				this.messages.push({
					role: 'assistant',
					content: data.reply,
					changes: data.changes || [],
					skipped: data.skipped || [],
					applied: false,
					isPreset: true,
					showChanges: (data.changes || []).length <= 15,
				})
			} catch (error) {
				const msg = (error.response && error.response.data && error.response.data.error) || 'Something went wrong. Please try again.'
				this.messages.push({ role: 'assistant', content: msg, error: true })
			} finally {
				this.loading = false
				this.scrollChat()
			}
		},

		async send() {
			const text = this.input.trim()
			if (!text || this.loading) return

			this.messages.push({ role: 'user', content: text })
			this.input = ''
			this.loading = true
			this.scrollChat()

			// History for the API: plain text turns only, skip client-side error bubbles
			const history = this.messages.filter(m => !m.error).map(m => ({ role: m.role, content: this.historyContent(m) }))

			try {
				const { data } = await juniperApi.post('/figma-ai/chat', {
					variables_css: this.setting.value,
					messages: history,
					client_id: this.clientId || null,
				})
				this.messages.push({
					role: 'assistant',
					content: data.reply,
					changes: data.changes || [],
					skipped: data.skipped || [],
					applied: false,
					showChanges: (data.changes || []).length <= 15,
				})
			} catch (error) {
				const data = (error.response && error.response.data) || {}
				const msg = data.error || 'Something went wrong. Please try again.'
				// 503 = no API key for the configured provider — point to the settings tab.
				this.messages.push({ role: 'assistant', content: msg, error: true, settingsLink: !!data.not_configured })
			} finally {
				this.loading = false
				this.scrollChat()
			}
		},

		applyChanges(msg) {
			if (!this.setting || msg.applied) return
			this.undoStack.push(this.setting.value)

			// Preset changes are computed against the original (pre-preset)
			// variables, so apply them onto that baseline — switching styles
			// replaces the previous preset instead of stacking scales/colors.
			let source = this.setting.value
			if (msg.isPreset) {
				if (this.presetBaseline === null) {
					this.presetBaseline = this.setting.value
				}
				source = this.presetBaseline
			}

			// Single pass over the whole block: full redesigns carry 1000+ changes,
			// per-change regexes over a ~200KB value would take seconds.
			const map = {}
			msg.changes.forEach(change => {
				map[change.name] = change.value
			})
			const found = {}
			const css = source.replace(/(--)([A-Za-z0-9_-]+)(\s*:\s*)[^;}]+/g, (full, dashes, name, sep) => {
				if (map[name] === undefined) return full
				found[name] = true
				return dashes + name + sep + map[name]
			})
			const missed = msg.changes.filter(change => !found[change.name]).map(change => change.name)
			if (missed.length) {
				msg.skipped = (msg.skipped || []).concat(missed)
			}

			this.setting.value = css
			msg.applied = true
			// Older preset bubbles can be re-applied to switch styles back
			this.messages.forEach(m => {
				if (m !== msg && m.isPreset && m.applied) {
					m.applied = false
				}
			})
			this.$emit('updatePreview')
		},

		undo() {
			if (!this.undoStack.length || !this.setting) return
			this.setting.value = this.undoStack.pop()
			// Re-enable Apply on the most recent applied message so the user can redo
			for (let i = this.messages.length - 1; i >= 0; i--) {
				if (this.messages[i].applied) {
					this.messages[i].applied = false
					break
				}
			}
			this.$emit('updatePreview')
		},

		isColor(value) {
			const v = (value || '').trim().toLowerCase()
			return /^#[0-9a-f]{3,8}$/.test(v) || v.startsWith('rgb')
		},

		historyContent(message) {
			if (message.role !== 'assistant' || !message.changes || !message.changes.length) {
				return message.content
			}

			// The model must know what was actually applied, not only what its prose
			// claimed. Distinct old/new transitions are enough to diagnose broad
			// accidental swaps without sending hundreds of variable names back.
			const transitions = []
			const seen = {}
			message.changes.forEach(change => {
				const transition = change.old + ' -> ' + change.value
				if (!seen[transition] && transitions.length < 12) {
					seen[transition] = true
					transitions.push(transition)
				}
			})
			const samples = message.changes.slice(0, 8).map(change => change.name).join(', ')
			return (
				message.content +
				'\n\n[Actual proposed result: ' +
				message.changes.length +
				' variables; value transitions: ' +
				transitions.join('; ') +
				'; sample variables: ' +
				samples +
				'. Applied to preview: ' +
				(message.applied ? 'yes' : 'no') +
				']'
			)
		},

		scrollChat() {
			this.$nextTick(() => {
				const el = this.$refs.chatArea
				if (el) el.scrollTop = el.scrollHeight
			})
		},
	},
	emits: ['updatePreview'],
}
</script>

<style scoped>
/* Flat "gh-*" design language shared with the Juniper settings pages:
   #e6e8ec borders, #f3f5f9 hover, #2b303b text, muted uppercase labels. */

#figmaAiModal .modal-content{
	border: 1px solid #e6e8ec;
	border-radius: 14px;
}

#figmaAiModal .fam-dialog{
	max-width: 500px;
}

#figmaAiModal .fam-header{
	border-bottom: 1px solid #f0f1f4;
	padding: 0.9rem 1.25rem;
}

#figmaAiModal .fam-title{
	display: flex;
	align-items: center;
	font-size: 1rem;
	font-weight: 700;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
	color: #2b303b;
	letter-spacing: -0.01em;
	margin-bottom: 2px;
}

#figmaAiModal .fam-title-icon{
	font-size: 0.9rem;
	line-height: 1;
}

#figmaAiModal .fam-sub{
	font-size: 0.78rem;
	color: #8a909c;
	line-height: 1.45;
	display: block;
}

#figmaAiModal .fam-close{
	margin-left: auto;
	opacity: 0.4;
}

/* ---- Flat buttons (gh-save-btn--sm) ---- */

#figmaAiModal .fam-btn{
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.3rem;
	padding: 0.25rem 0.6rem;
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 7px;
	color: #2b303b;
	font-size: 0.78rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

#figmaAiModal .fam-btn:hover:not(:disabled){
	background: #f3f5f9;
	border-color: #d6dae2;
}

#figmaAiModal .fam-btn:focus-visible{
	outline: 2px solid #2b6cff;
	outline-offset: 2px;
}

#figmaAiModal .fam-btn:disabled{
	opacity: 0.55;
	cursor: not-allowed;
}

#figmaAiModal .fam-btn--send{
	flex: 1;
	width: 38px;
	min-height: 29px;
	padding: 0;
}

#figmaAiModal .fam-btn--icon{
	color: #8a909c;
	width: 38px;
	height: 27px;
	padding: 0;
}

#figmaAiModal .fam-btn--icon:hover:not(:disabled),
#figmaAiModal .fam-btn--active{
	color: #2b303b;
	background: #f3f5f9;
}

/* ---- Chat ---- */

#figmaAiModal .fam-chat{
	min-height: 260px;
	max-height: 55vh;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

#figmaAiModal .fam-msg{
	display: flex;
}

#figmaAiModal .fam-msg--user{
	justify-content: flex-end;
}

#figmaAiModal .fam-bubble{
	max-width: 85%;
	padding: 8px 12px;
	border-radius: 12px;
	font-size: 13px;
	background: #f3f5f9;
	color: #2b303b;
	line-height: 1.5;
}

#figmaAiModal .fam-msg--user .fam-bubble{
	background: #2b303b;
	color: #fff;
}

#figmaAiModal .fam-bubble--error{
	background: #fdecea;
	color: #b02a37;
}

#figmaAiModal .fam-text{
	white-space: pre-wrap;
	word-break: break-word;
}

/* ---- Proposed changes (compact flat list, thin separators) ---- */

#figmaAiModal .fam-changes{
	margin-top: 8px;
	border-top: 1px solid #e3e6ea;
	padding-top: 6px;
}

#figmaAiModal .fam-changes-toggle{
	background: none;
	border: none;
	padding: 0;
	font-size: 12px;
	font-weight: 600;
	color: #8a909c;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 5px;
	transition: color 0.12s ease;
}

#figmaAiModal .fam-changes-toggle:hover{
	color: #2b303b;
}

#figmaAiModal .fam-changes-list{
	margin-top: 6px;
	max-height: 220px;
	overflow-y: auto;
}

#figmaAiModal .fam-change{
	display: flex;
	flex-direction: column;
	gap: 1px;
	font-size: 11px;
	padding: 4px 0;
	border-bottom: 1px solid #e3e6ea;
}

#figmaAiModal .fam-change:last-child{
	border-bottom: none;
}

#figmaAiModal .fam-change-name{
	font-size: 11px;
	color: #4a5260;
	word-break: break-all;
	background: transparent;
	padding: 0;
}

#figmaAiModal .fam-change-values{
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: #8a909c;
	flex-wrap: wrap;
}

#figmaAiModal .fam-old{
	text-decoration: line-through;
	opacity: 0.75;
}

#figmaAiModal .fam-old,
#figmaAiModal .fam-new{
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

#figmaAiModal .fam-new{
	color: #2b303b;
	font-weight: 600;
}

#figmaAiModal .fam-arrow{
	font-size: 10px;
}

#figmaAiModal .fam-swatch{
	display: inline-block;
	width: 11px;
	height: 11px;
	border-radius: 3px;
	border: 1px solid rgba(0, 0, 0, 0.12);
}

#figmaAiModal .fam-actions{
	margin-top: 8px;
}

#figmaAiModal .fam-applied{
	font-size: 12px;
	font-weight: 600;
	color: #198754;
}

#figmaAiModal .fam-skipped{
	margin-top: 6px;
	font-size: 11px;
	color: #b45309;
}

/* ---- Presets ---- */

#figmaAiModal .fam-presets-label{
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #8a909c;
	margin-bottom: 8px;
}

#figmaAiModal .fam-presets-grid{
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}

#figmaAiModal .fam-preset{
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 3px;
	padding: 10px 12px;
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	text-align: left;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease;
}

#figmaAiModal .fam-preset:hover:not(:disabled){
	background: #f3f5f9;
	border-color: #d6dae2;
}

#figmaAiModal .fam-preset:disabled{
	opacity: 0.55;
	cursor: default;
}

#figmaAiModal .fam-preset-swatches{
	display: inline-flex;
	gap: 4px;
	margin-bottom: 2px;
}

#figmaAiModal .fam-swatch--lg{
	width: 16px;
	height: 16px;
	border-radius: 5px;
}

#figmaAiModal .fam-preset-title{
	font-size: 13px;
	font-weight: 700;
	color: #2b303b;
}

#figmaAiModal .fam-preset-desc{
	font-size: 11px;
	color: #8a909c;
	line-height: 1.35;
}

#figmaAiModal .fam-presets-hint{
	font-size: 11px;
	color: #8a909c;
	margin-top: 8px;
}

/* ---- Footer ---- */

#figmaAiModal .fam-footer{
	flex-direction: column;
	align-items: stretch;
	gap: 8px;
	border-top: 1px solid #f0f1f4;
	padding: 0.75rem 1.25rem;
}

#figmaAiModal .fam-toolbar{
	display: flex;
	gap: 8px;
	width: 100%;
}

#figmaAiModal .fam-input-row{
	display: flex;
	gap: 8px;
	align-items: stretch;
}

#figmaAiModal .fam-input-row textarea{
	resize: none;
	font-size: 13px;
	border-color: #e6e8ec;
	border-radius: 10px;
	box-shadow: none;
}

#figmaAiModal .fam-input-row textarea:focus{
	border-color: #b9c0cc;
	box-shadow: none;
}

#figmaAiModal .fam-input-buttons{
	display: flex;
	flex-direction: column;
	gap: 4px;
}

/* ---- Inline link inside an error bubble (e.g. "Open AI settings") ---- */

#figmaAiModal .fam-link{
	display: inline-block;
	margin-left: 4px;
	color: #2b6cff;
	font-weight: 600;
	text-decoration: underline;
}

#figmaAiModal .fam-link:hover{
	color: #1f55d1;
}

/* ---- Pinned base: the modal must render identically in light AND dark
   dashboard mode, and with the system font stack (not the app's Roboto) —
   the #figmaAiModal prefix outranks design.scss and body.dashboard-dark. ---- */

#figmaAiModal .modal-content {
	background-color: #fff;
	color: #2b303b;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

#figmaAiModal .modal-body {
	padding: 1rem 1.25rem;
	background: transparent;
}

#figmaAiModal .modal-title {
	color: #2b303b;
}

#figmaAiModal textarea.form-control {
	background-color: #fff;
	color: #2b303b;
}

#figmaAiModal textarea.form-control::placeholder {
	color: #8a909c;
}

#figmaAiModal .btn-close {
	filter: none;
	background-color: transparent;
}

@media (max-width: 575.98px) {
	#figmaAiModal .fam-dialog{
		max-width: none;
	}
}
</style>
